/** Time, geography and robust-statistics helpers shared by the engine. Pure functions. */

import { fastZoneOffset, isFastZone } from '../shared/tzfast'

export const MINUTE = 60_000
export const HOUR = 3_600_000
export const DAY = 86_400_000

// ─── Time ──────────────────────────────────────────────────────────────────────

export function iso(ms: number): string {
  return new Date(ms).toISOString().replace('.000Z', 'Z')
}

export function isoOrNull(ms: number | null | undefined): string | null {
  return ms == null || !Number.isFinite(ms) ? null : iso(ms)
}

/** Parses an ISO-8601 instant that carries an explicit offset. */
export function parseTime(value: unknown): number | null {
  if (typeof value !== 'string' || !/(Z|[+-]\d\d:?\d\d)$/.test(value.trim())) return null
  const ms = Date.parse(value)
  return Number.isFinite(ms) ? ms : null
}

export const epochHour = (ms: number) => Math.floor(ms / HOUR)
export const floorHour = (ms: number) => Math.floor(ms / HOUR) * HOUR

const DURATION = /^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?)?$/

/** ISO-8601 durations as used by NWS grids: P7DT13H, PT1H, P3D, PT30M. */
export function parseDuration(text: string): number {
  const m = DURATION.exec(text ?? '')
  if (!m || text === 'P' || text === 'PT') throw new Error(`Unsupported duration ${text}`)
  const [d, h, mi, s] = m.slice(1).map((x) => (x ? Number(x) : 0))
  return d * DAY + h * HOUR + mi * MINUTE + s * 1000
}

/** "2026-09-26T18:00:00+00:00/PT3H" → [start, end) in epoch ms. */
export function parseInterval(validTime: string): [number, number] {
  const [startText, duration] = validTime.split('/')
  const start = parseTime(startText)
  if (start == null || !duration) throw new Error(`Invalid interval ${validTime}`)
  return [start, start + parseDuration(duration)]
}

const formatters = new Map<string, Intl.DateTimeFormat>()
function formatter(timeZone: string): Intl.DateTimeFormat {
  let f = formatters.get(timeZone)
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    })
    formatters.set(timeZone, f)
  }
  return f
}

export interface LocalParts {
  year: number
  month: number
  day: number
  hour: number
  minute: number
}

function intlParts(ms: number, timeZone: string): LocalParts {
  const parts: Record<string, number> = {}
  for (const p of formatter(timeZone).formatToParts(new Date(ms))) {
    if (p.type !== 'literal') parts[p.type] = Number(p.value)
  }
  return { year: parts.year, month: parts.month, day: parts.day, hour: parts.hour % 24, minute: parts.minute }
}

function intlOffset(ms: number, timeZone: string): number {
  const p = intlParts(ms, timeZone)
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute) - Math.floor(ms / MINUTE) * MINUTE
}

const BLOCK = 7 * DAY
interface OffsetBlock {
  before: number
  after: number
  /** First minute of `after` (Infinity when the block has one offset). */
  switchAt: number
}
const offsetCache = new Map<string, Map<number, OffsetBlock>>()

/**
 * UTC offset (ms) of `timeZone` at instant `ms`. Intl is slow (especially before it warms
 * up), so offsets are memoized per week: two lookups per week, and a week containing a DST
 * transition (its two ends differ) binary-searches the transition minute once (~14 lookups).
 * No real zone changes offset twice within a week.
 */
export function zoneOffset(ms: number, timeZone: string): number {
  // U.S. zones follow one rule: no Intl (milliseconds of CPU on a cold Workers isolate).
  const fast = fastZoneOffset(ms, timeZone)
  if (fast != null) return fast
  let blocks = offsetCache.get(timeZone)
  if (!blocks) {
    blocks = new Map()
    offsetCache.set(timeZone, blocks)
  }
  const block = Math.floor(ms / BLOCK)
  let v = blocks.get(block)
  if (v === undefined) {
    const start = block * BLOCK
    const end = start + BLOCK - MINUTE
    const before = intlOffset(start, timeZone)
    const after = intlOffset(end, timeZone)
    let switchAt = Number.POSITIVE_INFINITY
    if (before !== after) {
      let lo = start
      let hi = end
      while (hi - lo > MINUTE) {
        const mid = lo + Math.floor((hi - lo) / (2 * MINUTE)) * MINUTE
        if (intlOffset(mid, timeZone) === before) lo = mid
        else hi = mid
      }
      switchAt = hi
    }
    v = { before, after, switchAt }
    if (blocks.size > 5000) blocks.clear()
    blocks.set(block, v)
  }
  return ms < v.switchAt ? v.before : v.after
}

export function localParts(ms: number, timeZone: string): LocalParts {
  const d = new Date(ms + zoneOffset(ms, timeZone))
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth() + 1,
    day: d.getUTCDate(),
    hour: d.getUTCHours(),
    minute: d.getUTCMinutes(),
  }
}

export function localDate(ms: number, timeZone: string): string {
  const p = localParts(ms, timeZone)
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`
}

export function localHour(ms: number, timeZone: string): number {
  const p = localParts(ms, timeZone)
  return p.hour + p.minute / 60
}

/** Instant of local wall-clock `hour` on local `date` (YYYY-MM-DD). DST-safe. */
export function zonedTime(date: string, hour: number, timeZone: string): number {
  const [y, m, d] = date.split('-').map(Number)
  const guess = Date.UTC(y, m - 1, d) + hour * HOUR
  let ms = guess - zoneOffset(guess, timeZone)
  ms = guess - zoneOffset(ms, timeZone)
  return ms
}

export function addDays(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number)
  const t = new Date(Date.UTC(y, m - 1, d + days))
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`
}

export const pad = (n: number) => String(n).padStart(2, '0')

export function isValidTimeZone(timeZone: string | null | undefined): timeZone is string {
  if (!timeZone) return false
  if (isFastZone(timeZone)) return true
  try {
    formatter(timeZone)
    return true
  } catch {
    return false
  }
}

// ─── Numbers ───────────────────────────────────────────────────────────────────

export function finite(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

export function num(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value)
    return Number.isFinite(n) ? n : null
  }
  return null
}

export function round(value: number | null | undefined, digits = 1): number | null {
  if (value == null || !Number.isFinite(value)) return null
  const f = 10 ** digits
  return Math.round(value * f) / f
}

export const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v))

// ─── Geography ─────────────────────────────────────────────────────────────────

const R_EARTH = 6371.0088
const rad = (d: number) => (d * Math.PI) / 180

export function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const dp = rad(lat2 - lat1)
  const dl = rad(lon2 - lon1)
  const a = Math.sin(dp / 2) ** 2 + Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(dl / 2) ** 2
  return 2 * R_EARTH * Math.asin(Math.min(1, Math.sqrt(a)))
}

/** Initial great-circle bearing from point 1 to point 2, degrees clockwise from north. */
export function bearingDeg(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const p1 = rad(lat1)
  const p2 = rad(lat2)
  const dl = rad(lon2 - lon1)
  const x = Math.sin(dl) * Math.cos(p2)
  const y = Math.cos(p1) * Math.sin(p2) - Math.sin(p1) * Math.cos(p2) * Math.cos(dl)
  return ((Math.atan2(x, y) * 180) / Math.PI + 360) % 360
}

type Ring = number[][]

/** Even-odd ray casting on a GeoJSON linear ring of [lon, lat] pairs. */
export function pointInRing(lon: number, lat: number, ring: Ring): boolean {
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]
    const [xj, yj] = ring[j]
    if (yi > lat !== yj > lat) {
      const cross = ((xj - xi) * (lat - yi)) / (yj - yi) + xi
      if (lon < cross) inside = !inside
    }
  }
  return inside
}

/** Point-in-(Multi)Polygon for GeoJSON geometries, honoring holes. */
export function pointInGeometry(lon: number, lat: number, geometry: unknown): boolean {
  if (!geometry || typeof geometry !== 'object') return false
  const g = geometry as { type?: string; coordinates?: unknown }
  const polygons =
    g.type === 'Polygon'
      ? [g.coordinates as Ring[]]
      : g.type === 'MultiPolygon'
        ? (g.coordinates as Ring[][])
        : []
  for (const polygon of polygons ?? []) {
    if (!polygon?.length || !pointInRing(lon, lat, polygon[0])) continue
    if (!polygon.slice(1).some((hole) => pointInRing(lon, lat, hole))) return true
  }
  return false
}

// ─── Robust statistics ─────────────────────────────────────────────────────────

/** Weighted quantile with linear interpolation on the cumulative-weight midpoints. */
export function weightedQuantile(values: number[], weights: number[], q: number): number | null {
  const pairs: [number, number][] = []
  for (let i = 0; i < values.length; i++) {
    if (Number.isFinite(values[i]) && weights[i] > 0) pairs.push([values[i], weights[i]])
  }
  if (!pairs.length) return null
  if (pairs.length === 1) return pairs[0][0]
  pairs.sort((a, b) => a[0] - b[0])
  const total = pairs.reduce((s, p) => s + p[1], 0)
  let acc = 0
  const centers = pairs.map(([, w]) => {
    const c = (acc + w / 2) / total
    acc += w
    return c
  })
  if (q <= centers[0]) return pairs[0][0]
  if (q >= centers[centers.length - 1]) return pairs[pairs.length - 1][0]
  for (let i = 1; i < pairs.length; i++) {
    if (q <= centers[i]) {
      const f = (q - centers[i - 1]) / (centers[i] - centers[i - 1])
      return pairs[i - 1][0] + f * (pairs[i][0] - pairs[i - 1][0])
    }
  }
  return pairs[pairs.length - 1][0]
}

export const weightedMedian = (values: number[], weights: number[]) =>
  weightedQuantile(values, weights, 0.5)

/**
 * Weighted trimmed mean: removes `trim` of the total weight from each tail before
 * averaging (partial weights at the cut points). With < 4 members it is a plain mean.
 */
export function weightedTrimmedMean(values: number[], weights: number[], trim = 0.1): number | null {
  const pairs: [number, number][] = []
  for (let i = 0; i < values.length; i++) {
    if (Number.isFinite(values[i]) && weights[i] > 0) pairs.push([values[i], weights[i]])
  }
  if (!pairs.length) return null
  const total = pairs.reduce((s, p) => s + p[1], 0)
  if (pairs.length < 4) return pairs.reduce((s, p) => s + p[0] * p[1], 0) / total
  pairs.sort((a, b) => a[0] - b[0])
  const lo = total * trim
  const hi = total * (1 - trim)
  let acc = 0
  let sum = 0
  let kept = 0
  for (const [v, w] of pairs) {
    const start = acc
    const end = acc + w
    acc = end
    const keep = Math.max(0, Math.min(end, hi) - Math.max(start, lo))
    sum += v * keep
    kept += keep
  }
  return kept > 0 ? sum / kept : pairs.reduce((s, p) => s + p[0] * p[1], 0) / total
}

// Allocation-free variants for the fusion hot loop: the first `count` entries of `values` /
// `weights`, scratch index buffer `order` (length ≥ count). Same arithmetic, in the same order,
// as weightedTrimmedMean / weightedQuantile above, so results are identical.

function sortedFinite(values: ArrayLike<number>, weights: ArrayLike<number>, count: number, order: Int32Array): number {
  let m = 0
  for (let i = 0; i < count; i++) {
    if (!Number.isFinite(values[i]) || !(weights[i] > 0)) continue
    // Stable insertion sort by value (ties keep their order, like Array.prototype.sort).
    let j = m++
    while (j > 0 && values[order[j - 1]] > values[i]) {
      order[j] = order[j - 1]
      j--
    }
    order[j] = i
  }
  return m
}

export function trimmedMeanN(values: ArrayLike<number>, weights: ArrayLike<number>, count: number, order: Int32Array, trim = 0.1): number {
  let total = 0
  let plain = 0
  let pairs = 0
  for (let i = 0; i < count; i++) {
    if (!Number.isFinite(values[i]) || !(weights[i] > 0)) continue
    total += weights[i]
    plain += values[i] * weights[i]
    pairs++
  }
  if (!pairs) return Number.NaN
  if (pairs < 4) return plain / total
  const m = sortedFinite(values, weights, count, order)
  const lo = total * trim
  const hi = total * (1 - trim)
  let acc = 0
  let sum = 0
  let kept = 0
  for (let j = 0; j < m; j++) {
    const i = order[j]
    const start = acc
    const end = acc + weights[i]
    acc = end
    const keep = Math.max(0, Math.min(end, hi) - Math.max(start, lo))
    sum += values[i] * keep
    kept += keep
  }
  return kept > 0 ? sum / kept : plain / total
}

export function quantileN(values: ArrayLike<number>, weights: ArrayLike<number>, count: number, order: Int32Array, q: number): number {
  const m = sortedFinite(values, weights, count, order)
  if (!m) return Number.NaN
  if (m === 1) return values[order[0]]
  let total = 0
  let beforeLast = 0
  for (let j = 0; j < m; j++) {
    if (j === m - 1) beforeLast = total
    total += weights[order[j]]
  }
  if (q > (weights[order[0]] / 2) / total && q >= (beforeLast + weights[order[m - 1]] / 2) / total) return values[order[m - 1]]
  let acc = 0
  let prevCenter = 0
  for (let j = 0; j < m; j++) {
    const w = weights[order[j]]
    const center = (acc + w / 2) / total
    acc += w
    if (j === 0 && q <= center) return values[order[0]]
    if (j > 0 && q <= center) {
      const f = (q - prevCenter) / (center - prevCenter)
      return values[order[j - 1]] + f * (values[order[j]] - values[order[j - 1]])
    }
    prevCenter = center
  }
  return values[order[m - 1]]
}

export function meanN(values: ArrayLike<number>, weights: ArrayLike<number>, count: number): number {
  let s = 0
  let w = 0
  for (let i = 0; i < count; i++) {
    if (Number.isFinite(values[i]) && weights[i] > 0) {
      s += values[i] * weights[i]
      w += weights[i]
    }
  }
  return w > 0 ? s / w : Number.NaN
}

export function stdN(values: ArrayLike<number>, weights: ArrayLike<number>, count: number): number {
  const mean = meanN(values, weights, count)
  if (!Number.isFinite(mean)) return Number.NaN
  let s = 0
  let w = 0
  for (let i = 0; i < count; i++) {
    if (Number.isFinite(values[i]) && weights[i] > 0) {
      s += weights[i] * (values[i] - mean) ** 2
      w += weights[i]
    }
  }
  return w > 0 ? Math.sqrt(s / w) : Number.NaN
}

export function weightedMean(values: number[], weights: number[]): number | null {
  let s = 0
  let w = 0
  for (let i = 0; i < values.length; i++) {
    if (Number.isFinite(values[i]) && weights[i] > 0) {
      s += values[i] * weights[i]
      w += weights[i]
    }
  }
  return w > 0 ? s / w : null
}

export function weightedStd(values: number[], weights: number[]): number | null {
  const mean = weightedMean(values, weights)
  if (mean == null) return null
  let s = 0
  let w = 0
  for (let i = 0; i < values.length; i++) {
    if (Number.isFinite(values[i]) && weights[i] > 0) {
      s += weights[i] * (values[i] - mean) ** 2
      w += weights[i]
    }
  }
  return w > 0 ? Math.sqrt(s / w) : null
}

/**
 * Weighted circular mean of meteorological directions (degrees). Returns the mean
 * direction and the resultant length (0 = no agreement, 1 = identical directions).
 */
export function circularMean(
  degrees: number[],
  weights: number[],
): { deg: number; r: number } | null {
  let x = 0
  let y = 0
  let w = 0
  for (let i = 0; i < degrees.length; i++) {
    if (!Number.isFinite(degrees[i]) || !(weights[i] > 0)) continue
    x += weights[i] * Math.sin(rad(degrees[i]))
    y += weights[i] * Math.cos(rad(degrees[i]))
    w += weights[i]
  }
  if (w <= 0) return null
  const r = Math.hypot(x, y) / w
  const deg = ((Math.atan2(x, y) * 180) / Math.PI + 360) % 360
  return { deg, r }
}

export function mean(values: number[]): number | null {
  const v = values.filter(Number.isFinite)
  return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null
}

export function maxOf(values: (number | null)[]): number | null {
  let m: number | null = null
  for (const v of values) if (v != null && Number.isFinite(v) && (m == null || v > m)) m = v
  return m
}

export function minOf(values: (number | null)[]): number | null {
  let m: number | null = null
  for (const v of values) if (v != null && Number.isFinite(v) && (m == null || v < m)) m = v
  return m
}
